import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ActionIcon,
  Button,
  Modal,
  Progress,
  Table,
  Badge,
  Group,
  Loader,
  Paper,
  Pagination,
  ScrollArea,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import React, { useCallback, useMemo, useState } from "react";
import {
  CloudUpload,
  Eye,
  Folder,
  FolderPlus,
  Pencil,
  PlayCircle,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  Video,
} from "lucide-react";

import Head from "../components/common/Head";
import useValidate from "../hooks/useValidate";
import {
  collectionAPI,
  DEFAULT_VIDEO_RESOLUTIONS,
  formatDuration,
  formatFileSize,
  getVideoStatusColor,
  getVideoStatusText,
  uploadVideoWithTus,
  videoAPI,
} from "../services/bunnyApi";

export default function BunnyLibrary() {
  useValidate();

  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState("");
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [collectionPage, setCollectionPage] = useState(1);
  const [collectionPageSize, setCollectionPageSize] = useState(10);
  const [videoPage, setVideoPage] = useState(1);
  const [videoPageSize, setVideoPageSize] = useState(10);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isPlayerModalOpen, setIsPlayerModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [uploadTasks, setUploadTasks] = useState([]);
  const [files, setFiles] = useState([]);
  const [createName, setCreateName] = useState("");
  const [editName, setEditName] = useState("");

  const {
    data: collectionsData,
    isLoading: collectionsLoading,
    refetch: refetchCollections,
  } = useQuery({
    queryKey: ["bunny-collections", searchText, collectionPage, collectionPageSize],
    queryFn: () => collectionAPI.getAll(collectionPage, collectionPageSize, searchText),
  });

  const {
    data: videosData,
    isLoading: videosLoading,
    refetch: refetchVideos,
  } = useQuery({
    queryKey: ["bunny-videos", selectedCollection?.guid, videoPage, videoPageSize],
    queryFn: () =>
      selectedCollection
        ? videoAPI.getAll(videoPage, videoPageSize, "", selectedCollection.guid)
        : Promise.resolve({ items: [], totalItems: 0 }),
    enabled: !!selectedCollection,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.items?.length) {
        const hasProcessing = data.items.some((v) => v.status >= 0 && v.status <= 3);
        return hasProcessing ? 5000 : false;
      }
      return false;
    },
  });

  const createCollectionMutation = useMutation({
    mutationFn: collectionAPI.create,
    onSuccess: () => {
      notifications.show({ message: "Collection created successfully!", color: "green" });
      queryClient.invalidateQueries({ queryKey: ["bunny-collections"] });
      setIsCreateModalOpen(false);
      setCreateName("");
    },
    onError: (err) => {
      notifications.show({
        message: err?.response?.data?.message || "Failed to create collection",
        color: "red",
      });
    },
  });

  const updateCollectionMutation = useMutation({
    mutationFn: ({ id, data }) => collectionAPI.update(id, data),
    onSuccess: () => {
      notifications.show({ message: "Collection updated successfully!", color: "green" });
      queryClient.invalidateQueries({ queryKey: ["bunny-collections"] });
      setIsEditModalOpen(false);
      setEditName("");
    },
    onError: (err) => {
      notifications.show({
        message: err?.response?.data?.message || "Failed to update collection",
        color: "red",
      });
    },
  });

  const deleteCollectionMutation = useMutation({
    mutationFn: collectionAPI.delete,
    onSuccess: () => {
      notifications.show({ message: "Collection deleted successfully!", color: "green" });
      queryClient.invalidateQueries({ queryKey: ["bunny-collections"] });
      if (selectedCollection) setSelectedCollection(null);
    },
    onError: (err) => {
      notifications.show({
        message: err?.response?.data?.message || "Failed to delete collection",
        color: "red",
      });
    },
  });

  const deleteVideoMutation = useMutation({
    mutationFn: videoAPI.delete,
    onSuccess: () => {
      notifications.show({ message: "Video deleted successfully!", color: "green" });
      queryClient.invalidateQueries({ queryKey: ["bunny-videos"] });
      queryClient.invalidateQueries({ queryKey: ["bunny-collections"] });
    },
    onError: (err) => {
      notifications.show({
        message: err?.response?.data?.message || "Failed to delete video",
        color: "red",
      });
    },
  });

  const collectionItems = collectionsData?.items || [];
  const collectionTotal = collectionsData?.totalItems ?? 0;
  const videoItems = videosData?.items || [];
  const videoTotal = videosData?.totalItems ?? 0;

  const handleUpload = useCallback(async () => {
    if (!selectedCollection || files.length === 0) {
      notifications.show({ message: "Please select video files to upload", color: "yellow" });
      return;
    }

    const newTasks = [];

    for (const file of files) {
      if (!(file instanceof File)) continue;

      try {
        const videoEntry = await videoAPI.create({
          title: file.name.replace(/\\.[^/.]+$/, ""),
          collectionId: selectedCollection.guid,
          enabledResolutions: DEFAULT_VIDEO_RESOLUTIONS,
        });

        newTasks.push({
          id: `${file.name}-${file.size}-${file.lastModified}`,
          file,
          videoId: videoEntry.guid,
          progress: 0,
          status: "pending",
        });
      } catch {
        notifications.show({
          message: `Failed to create video entry for ${file.name}`,
          color: "red",
        });
      }
    }

    setUploadTasks(newTasks);

    for (const task of newTasks) {
      setUploadTasks((prev) =>
        prev.map((t) => (t.id === task.id ? { ...t, status: "uploading" } : t))
      );

      try {
        await uploadVideoWithTus({
          file: task.file,
          videoId: task.videoId,
          onProgress: (progress) => {
            setUploadTasks((prev) =>
              prev.map((t) => (t.id === task.id ? { ...t, progress } : t))
            );
          },
          onSuccess: () => {
            setUploadTasks((prev) =>
              prev.map((t) =>
                t.id === task.id
                  ? { ...t, status: "completed", progress: 100 }
                  : t
              )
            );
            queryClient.invalidateQueries({ queryKey: ["bunny-videos"] });
            queryClient.invalidateQueries({ queryKey: ["bunny-collections"] });
          },
          onError: (error) => {
            setUploadTasks((prev) =>
              prev.map((t) =>
                t.id === task.id
                  ? { ...t, status: "error", error: error.message }
                  : t
              )
            );
          },
        });
      } catch (error) {
        setUploadTasks((prev) =>
          prev.map((t) =>
            t.id === task.id
              ? {
                  ...t,
                  status: "error",
                  error: error?.message || "Upload failed",
                }
              : t
          )
        );
      }
    }

    notifications.show({ message: "Upload started!", color: "green" });
  }, [selectedCollection, files, queryClient]);

  const handleCreateCollection = () => {
    const name = createName.trim();
    if (!name) {
      notifications.show({ message: "Please enter a collection name", color: "red" });
      return;
    }
    createCollectionMutation.mutate({ name });
  };

  const handleEditCollection = () => {
    if (!selectedCollection) return;
    const name = editName.trim();
    if (!name) {
      notifications.show({ message: "Please enter a collection name", color: "red" });
      return;
    }
    updateCollectionMutation.mutate({
      id: selectedCollection.guid,
      data: { name },
    });
  };

  const handleOpenEditModal = (collection) => {
    setSelectedCollection(collection);
    setEditName(collection?.name || "");
    setIsEditModalOpen(true);
  };

  const handleViewCollection = (collection) => {
    setSelectedCollection(collection);
    setVideoPage(1);
    setIsVideoModalOpen(true);
  };

  const handleOpenUploadModal = (collection) => {
    setSelectedCollection(collection);
    setFiles([]);
    setUploadTasks([]);
    setIsUploadModalOpen(true);
  };

  const handlePlayVideo = (video) => {
    setSelectedVideo(video);
    setIsPlayerModalOpen(true);
  };

  const totalCollectionsPages = Math.max(1, Math.ceil(collectionTotal / collectionPageSize));
  const totalVideoPages = Math.max(1, Math.ceil(videoTotal / videoPageSize));

  const pageSizeOptions = useMemo(
    () => [
      { value: "10", label: "10 / page" },
      { value: "20", label: "20 / page" },
      { value: "50", label: "50 / page" },
    ],
    []
  );

  const allUploadsComplete =
    uploadTasks.length > 0 &&
    uploadTasks.every((t) => t.status === "completed" || t.status === "error");

  return (
    <div className="flex flex-col gap-4">
      <Head title="Bulk Upload" />

      <Paper className="app-card rounded-xl shadow-sm p-5">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 pb-4 border-b app-border-color">
          <div>
            <Group gap="sm">
              <Video className="text-blue-600" size={20} />
              <Title order={3} className="!m-0">
                Bulk Upload
              </Title>
            </Group>
            <Text className="app-text-muted" size="sm" mt={4}>
              Manage collections
            </Text>
          </div>
          <Group>
            <Button variant="default" leftSection={<RefreshCw size={16} />} onClick={() => refetchCollections()}>
              Refresh
            </Button>
            <Button leftSection={<FolderPlus size={16} />} onClick={() => setIsCreateModalOpen(true)} className="!bg-blue-600 hover:!bg-blue-500 !border-none">
              New Collection
            </Button>
          </Group>
        </div>

        <div className="mb-6">
          <TextInput
            placeholder="Search collections..."
            leftSection={<Search size={16} className="app-text-muted" />}
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setCollectionPage(1);
            }}
            className="max-w-md"
          />
        </div>

        {collectionsLoading ? (
          <div className="flex justify-center py-20">
            <Loader />
          </div>
        ) : collectionItems.length > 0 ? (
          <Stack gap="sm">
            <ScrollArea>
              <Table withTableBorder highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Collection</Table.Th>
                    <Table.Th w={120}>Videos</Table.Th>
                    <Table.Th w={140}>Size</Table.Th>
                    <Table.Th w={220}>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {collectionItems.map((c) => (
                    <Table.Tr key={c.guid}>
                      <Table.Td>
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg flex items-center justify-center">
                            <Folder className="text-white" size={20} />
                          </div>
                          <div className="min-w-0">
                            <div className="font-semibold truncate">{c.name}</div>
                            <div className="text-xs app-text-muted truncate">ID: {c.guid}</div>
                          </div>
                        </div>
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          color="blue"
                          variant="filled"
                          style={{
                            backgroundColor: (c.videoCount ?? 0) > 0 ? "#2563eb" : "#525252",
                          }}
                        >
                          {c.videoCount ?? 0}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text className="app-text-muted" size="sm">
                          {formatFileSize(c.totalSize)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          <ActionIcon variant="subtle" onClick={() => handleViewCollection(c)} aria-label="View videos">
                            <Eye size={16} />
                          </ActionIcon>
                          <ActionIcon variant="subtle" onClick={() => handleOpenUploadModal(c)} aria-label="Upload video">
                            <CloudUpload size={16} />
                          </ActionIcon>
                          <ActionIcon variant="subtle" onClick={() => handleOpenEditModal(c)} aria-label="Edit collection">
                            <Pencil size={16} />
                          </ActionIcon>
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={() => {
                              if (window.confirm("Delete collection? This will not delete videos.")) {
                                deleteCollectionMutation.mutate(c.guid);
                              }
                            }}
                            aria-label="Delete collection"
                          >
                            <Trash2 size={16} />
                          </ActionIcon>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>

            <Group justify="space-between" align="center">
              <Text className="app-text-muted" size="sm">
                {(collectionPage - 1) * collectionPageSize + 1}-
                {Math.min(collectionPage * collectionPageSize, collectionTotal)} of {collectionTotal} collections
              </Text>
              <Group gap="sm">
                <Select
                  data={pageSizeOptions}
                  value={String(collectionPageSize)}
                  onChange={(v) => {
                    const next = Number(v || 10);
                    setCollectionPageSize(next);
                    setCollectionPage(1);
                  }}
                  w={120}
                />
                <Pagination
                  total={totalCollectionsPages}
                  value={collectionPage}
                  onChange={setCollectionPage}
                />
              </Group>
            </Group>
          </Stack>
        ) : (
          <Stack align="center" py="xl">
            <Text className="app-text-muted">No collections found. Create your first collection!</Text>
            <Button leftSection={<Plus size={16} />} onClick={() => setIsCreateModalOpen(true)} className="!bg-blue-600 !border-none">
              Create Collection
            </Button>
          </Stack>
        )}
      </Paper>

      {/* Create Collection Modal */}
      <Modal
        opened={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setCreateName("");
        }}
        title={
          <Group gap="sm">
            <FolderPlus size={18} className="text-blue-600" />
            <Text fw={600}>Create New Collection</Text>
          </Group>
        }
      >
        <Stack mt="md">
          <TextInput
            label="Collection Name"
            placeholder="Enter collection name"
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => { setIsCreateModalOpen(false); setCreateName(""); }}>
              Cancel
            </Button>
            <Button loading={createCollectionMutation.isPending} onClick={handleCreateCollection} className="!bg-blue-600 !border-none">
              Create
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Edit Collection Modal */}
      <Modal
        opened={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditName("");
        }}
        title={
          <Group gap="sm">
            <Pencil size={18} className="text-blue-600" />
            <Text fw={600}>Edit Collection</Text>
          </Group>
        }
      >
        <Stack mt="md">
          <TextInput
            label="Collection Name"
            placeholder="Enter collection name"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => { setIsEditModalOpen(false); setEditName(""); }}>
              Cancel
            </Button>
            <Button loading={updateCollectionMutation.isPending} onClick={handleEditCollection} className="!bg-blue-600 !border-none">
              Update
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Videos Modal */}
      <Modal
        opened={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        size="xl"
        title={
          <Group gap="sm">
            <Folder size={18} className="text-blue-600" />
            <Text fw={600}>{selectedCollection?.name} — Videos</Text>
          </Group>
        }
      >
        <Group justify="space-between" mb="md">
          <Button
            leftSection={<CloudUpload size={16} />}
            onClick={() => {
              setIsVideoModalOpen(false);
              handleOpenUploadModal(selectedCollection);
            }}
            className="!bg-blue-600 !border-none"
          >
            Upload Video
          </Button>
          <Button variant="default" onClick={() => setIsVideoModalOpen(false)}>
            Close
          </Button>
        </Group>

        {videosLoading ? (
          <div className="flex justify-center py-10">
            <Loader />
          </div>
        ) : videoItems.length > 0 ? (
          <Stack gap="sm">
            <ScrollArea>
              <Table withTableBorder highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Video</Table.Th>
                    <Table.Th w={160}>Status</Table.Th>
                    <Table.Th w={160}>Resolution</Table.Th>
                    <Table.Th w={140}>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {videoItems.map((v) => (
                    <Table.Tr key={v.guid}>
                      <Table.Td>
                        <div className="flex items-center gap-3">
                          <div className="w-20 h-12 app-card rounded overflow-hidden relative group">
                            <img
                              src={videoAPI.getThumbnailUrl(v.guid, v.thumbnailFileName, v.videoLibraryId)}
                              alt={v.title}
                              style={{ width: 80, height: 48, objectFit: "cover" }}
                              onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src =
                                  "data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D'http%3A//www.w3.org/2000/svg'%20width%3D'160'%20height%3D'96'%20viewBox%3D'0%200%20160%2096'%3E%3Crect%20width%3D'160'%20height%3D'96'%20rx%3D'8'%20fill%3D'%23111827'/%3E%3Cpath%20d%3D'M68%2031l34%2017-34%2017V31z'%20fill%3D'%239CA3AF'/%3E%3C/svg%3E";
                              }}
                            />
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                              <PlayCircle className="text-white" size={24} onClick={() => handlePlayVideo(v)} />
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">{v.title}</div>
                            <div className="text-xs app-text-muted">
                              {v.length > 0 ? formatDuration(v.length) : "Processing..."} •{" "}
                              {v.storageSize > 0 ? formatFileSize(v.storageSize) : "-"}
                            </div>
                          </div>
                        </div>
                      </Table.Td>
                      <Table.Td>
                        <Stack gap={4}>
                          <Badge
                            variant="light"
                            color={getVideoStatusColor(v.status) === "default" ? "gray" : getVideoStatusColor(v.status)}
                          >
                            {getVideoStatusText(v.status)}
                          </Badge>
                          {(v.status === 2 || v.status === 3) && (
                            <Progress value={v.encodeProgress || 0} size="sm" />
                          )}
                        </Stack>
                      </Table.Td>
                      <Table.Td>
                        <Text className="app-text-muted" size="sm">
                          {v.availableResolutions || "-"}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          <ActionIcon variant="subtle" onClick={() => handlePlayVideo(v)} aria-label="Play">
                            <PlayCircle size={16} />
                          </ActionIcon>
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={() => {
                              if (window.confirm("Delete video?")) deleteVideoMutation.mutate(v.guid);
                            }}
                            aria-label="Delete"
                          >
                            <Trash2 size={16} />
                          </ActionIcon>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>

            <Group justify="space-between" align="center">
              <Text className="app-text-muted" size="sm">
                {(videoPage - 1) * videoPageSize + 1}-{Math.min(videoPage * videoPageSize, videoTotal)} of {videoTotal} videos
              </Text>
              <Group gap="sm">
                <Select
                  data={pageSizeOptions}
                  value={String(videoPageSize)}
                  onChange={(v) => {
                    const next = Number(v || 10);
                    setVideoPageSize(next);
                    setVideoPage(1);
                  }}
                  w={120}
                />
                <Pagination total={totalVideoPages} value={videoPage} onChange={setVideoPage} />
              </Group>
            </Group>
          </Stack>
        ) : (
          <Text className="app-text-muted" ta="center" py="xl">
            No videos in this collection yet
          </Text>
        )}
      </Modal>

      {/* Upload Modal */}
      <Modal
        opened={isUploadModalOpen}
        onClose={() => {
          if (uploadTasks.some((t) => t.status === "uploading")) {
            notifications.show({ message: "Please wait for uploads to complete", color: "yellow" });
            return;
          }
          setIsUploadModalOpen(false);
          setFiles([]);
          setUploadTasks([]);
        }}
        size="lg"
        title={
          <Group gap="sm">
            <CloudUpload size={18} className="text-blue-600" />
            <Text fw={600}>Upload Videos to {selectedCollection?.name}</Text>
          </Group>
        }
      >
        <Stack>
          <Group justify="space-between">
            <input
              type="file"
              accept="video/*"
              multiple
              onChange={(e) => {
                const list = Array.from(e.target.files || []);
                setFiles(list);
              }}
            />
            {uploadTasks.length === 0 ? (
              <Group>
                <Button
                  variant="default"
                  onClick={() => {
                    setIsUploadModalOpen(false);
                    setFiles([]);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  leftSection={<CloudUpload size={16} />}
                  onClick={handleUpload}
                  disabled={files.length === 0}
                  className="!bg-blue-600 !border-none disabled:!opacity-50"
                >
                  Start Upload
                </Button>
              </Group>
            ) : (
              <Button
                variant="default"
                onClick={() => {
                  if (uploadTasks.some((t) => t.status === "uploading")) {
                    notifications.show({ message: "Please wait for uploads to complete", color: "yellow" });
                    return;
                  }
                  setIsUploadModalOpen(false);
                  setFiles([]);
                  setUploadTasks([]);
                  refetchVideos();
                }}
                disabled={uploadTasks.some((t) => t.status === "uploading")}
              >
                {allUploadsComplete ? "Close" : "Uploading..."}
              </Button>
            )}
          </Group>

          {uploadTasks.length === 0 ? (
            files.length > 0 ? (
              <div className="p-4 app-card rounded-lg">
                <Text className="app-text" fw={600}>
                  {files.length} video(s) selected
                </Text>
                <Text className="app-text-muted" size="sm" mt={4}>
                  Total size: {formatFileSize(files.reduce((acc, f) => acc + (f.size || 0), 0))}
                </Text>
              </div>
            ) : (
              <Text className="app-text-muted">Choose video files to upload (MP4, WebM, MOV).</Text>
            )
          ) : (
            <Stack gap="sm">
              {uploadTasks.map((task) => (
                <div key={task.id} className="p-4 app-card rounded-lg">
                  <Group justify="space-between" mb="xs">
                    <Group gap="xs">
                      <Video className="text-blue-400" size={16} />
                      <Text className="app-text" fw={600} lineClamp={1}>
                        {task.file.name}
                      </Text>
                    </Group>
                    <Badge
                      variant="light"
                      color={
                        task.status === "completed"
                          ? "green"
                          : task.status === "error"
                            ? "red"
                            : task.status === "uploading"
                              ? "blue"
                              : "gray"
                      }
                    >
                      {task.status === "completed"
                        ? "Completed"
                        : task.status === "error"
                          ? "Failed"
                          : task.status === "uploading"
                            ? "Uploading"
                            : "Pending"}
                    </Badge>
                  </Group>
                  <Progress value={task.progress} size="sm" />
                  {task.error && (
                    <Text c="red" size="xs" mt={4}>
                      {task.error}
                    </Text>
                  )}
                  <Text className="app-text-muted" size="xs" mt={4}>
                    {formatFileSize(task.file.size)}
                  </Text>
                </div>
              ))}

              {allUploadsComplete && (
                <div className="p-4 rounded-lg text-center app-card-elevated">
                  <Text c="green" fw={600}>
                    All uploads completed! Videos are now being processed.
                  </Text>
                </div>
              )}
            </Stack>
          )}
        </Stack>
      </Modal>

      {/* Video Player Modal */}
      <Modal
        opened={isPlayerModalOpen}
        onClose={() => {
          setIsPlayerModalOpen(false);
          setSelectedVideo(null);
        }}
        size="xl"
        title={
          <Group gap="sm">
            <PlayCircle size={18} className="text-blue-600" />
            <Text fw={600}>{selectedVideo?.title}</Text>
          </Group>
        }
      >
        {selectedVideo && (
          <div className="w-full" style={{ aspectRatio: "16/9", maxHeight: "80vh", margin: "0 auto" }}>
            <iframe
              src={videoAPI.getPlayerUrl(selectedVideo.guid)}
              loading="lazy"
              style={{
                border: "none",
                width: "100%",
                height: "100%",
                borderRadius: "8px",
              }}
              allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              title={selectedVideo.title}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}
